import type { FieldDef, RepeaterItem } from '@jfh/intake-shared';
import {
  CONTACT_PRIMARY_KEY,
  isContactMetaKey,
  isItemListField,
  missingAddressComponents,
  repeaterErrors,
  resolveRepeaterSubFields,
  resolveStaticOptions,
  visibleFields,
} from '@jfh/intake-shared';

/** A composite address value. Stored structured; joined for display/submit. */
export interface AddressValue {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  /** When true, this address mirrors the first address on the form. */
  sameAsFirst?: boolean;
}

/** All values the renderer tracks, keyed by field id. */
export type FormValues = Record<string, unknown>;

/** A page of fields, titled by the `heading` that started it. */
export interface Page {
  title: string;
  /** The line under the step title (the heading's help text). */
  intro?: string;
  fields: FieldDef[];
}

/**
 * Split fields into pages on `heading` fields. Fields before the first heading
 * form an initial "Your details" page. Headings themselves are page titles,
 * never rendered as inputs.
 */
export function buildPages(fields: FieldDef[]): Page[] {
  const pages: Page[] = [];
  let cur: Page | null = null;
  for (const f of fields) {
    if (f.type === 'heading') {
      cur = { title: f.label, intro: f.help?.trim() || undefined, fields: [] };
      pages.push(cur);
    } else {
      if (!cur) {
        cur = { title: 'Your details', fields: [] };
        pages.push(cur);
      }
      cur.fields.push(f);
    }
  }
  return pages.length ? pages : [{ title: 'Your details', fields }];
}

/** A field that the applicant can actually fill in (not layout/hidden). */
export function isInputField(field: FieldDef): boolean {
  return field.type !== 'heading' && field.type !== 'hidden';
}

export function isAddressValue(v: unknown): v is AddressValue {
  return (
    typeof v === 'object' &&
    v !== null &&
    'line1' in v &&
    'postcode' in v
  );
}

const EMPTY_ADDRESS: AddressValue = {
  line1: '',
  line2: '',
  line3: '',
  city: '',
  county: '',
  postcode: '',
  country: '',
};

export function asAddress(v: unknown): AddressValue {
  return isAddressValue(v) ? v : { ...EMPTY_ADDRESS };
}

/** A repeater value is an array of plain objects (one per item). */
export function isRepeaterValue(v: unknown): v is RepeaterItem[] {
  return (
    Array.isArray(v) &&
    v.every(
      (it) => typeof it === 'object' && it !== null && !Array.isArray(it),
    )
  );
}

/** Render a repeater value (array of item objects) for the review screen. */
function stringifyRepeater(items: RepeaterItem[]): string {
  return items
    .map((it) =>
      Object.entries(it)
        .filter(([k]) => !isContactMetaKey(k))
        .map(([, cell]) => stringifyValue(cell))
        .filter((s) => s.trim())
        .join(' · '),
    )
    .filter((line) => line.trim())
    .join('; ');
}

/**
 * Country code → name map (from the iQ /countries options), so the review shows
 * the country Name while the stored/submitted value is the Code. Set on form load.
 */
let countryNames: Record<string, string> = {};
export function setCountryNames(opts: { value: string; label: string }[] | undefined): void {
  const next: Record<string, string> = {};
  for (const o of opts ?? []) next[o.value] = o.label;
  countryNames = next;
}

/** A human-readable single-line rendering of any value (review + conditions). */
export function stringifyValue(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : '';
  if (Array.isArray(v)) {
    if (isRepeaterValue(v) && v.length > 0) return stringifyRepeater(v);
    return v.join(', ');
  }
  if (isAddressValue(v)) {
    const country = countryNames[v.country] ?? v.country;
    return [v.line1, v.line2, v.line3, v.city, v.county, v.postcode, country]
      .filter((s) => s && s.trim())
      .join(', ');
  }
  return String(v);
}

/** Whether a value counts as "provided" for required-field validation. */
export function hasValue(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'boolean') return v === true;
  if (Array.isArray(v)) {
    if (isRepeaterValue(v)) {
      return v.some((it) =>
        Object.values(it).some(
          (cell) => typeof cell === 'string' && cell.trim().length > 0,
        ),
      );
    }
    return v.length > 0;
  }
  if (typeof v === 'string') return v.trim().length > 0;
  if (isAddressValue(v)) {
    return [v.line1, v.line2, v.line3, v.city, v.county, v.postcode, v.country].some(
      (s) => typeof s === 'string' && s.trim().length > 0,
    );
  }
  return true;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A question's label inside a sentence (F10 P6): "Trading name" → "trading
 * name", "Your name" → "name", while "VAT number" keeps its capitals.
 */
export function inSentence(label: string): string {
  const l = label.trim().replace(/[:?*]+$/, '').replace(/^your\s+/i, '');
  // Lower ordinary words ("Company Type" → "company type"); keep acronyms (VAT, iQ).
  return l.replace(/\b[A-Z][a-z]+\b/g, (w) => w.toLowerCase());
}

const CHOICE_TYPES = new Set(['select', 'radio', 'checkbox', 'multiselect', 'country']);

/** The message for a required question left empty: "Enter your trading name." */
export function requiredMessage(field: FieldDef): string {
  if (field.label.trim().endsWith('?')) return `Answer “${field.label.trim()}”.`;
  if (CHOICE_TYPES.has(field.type)) return `Choose your ${inSentence(field.label)}.`;
  if (field.type === 'file') return `Add your ${inSentence(field.label)}.`;
  return `Enter your ${inSentence(field.label)}.`;
}

/**
 * Validate one field's current value. Returns an error message or null.
 * Only validates fields that are currently visible (callers filter).
 */
export function validateField(
  field: FieldDef,
  value: unknown,
  /** Contact addresses not to re-check: copies of a Companies House address. */
  lenientAddresses: readonly string[] = [],
): string | null {
  // Address validation is per-component (enabled + required sub-fields).
  if (field.type === 'address') {
    const missing = missingAddressComponents(field, value);
    if (missing.length) {
      const parts = missing.map((m) => m.toLowerCase());
      const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
      return `Enter the ${list} for the ${inSentence(field.label)}.`;
    }
    return null;
  }

  // Repeater / contact validation is per-item (min count + required subs).
  if (isItemListField(field.type)) {
    const errs = repeaterErrors(field, value, lenientAddresses);
    return errs.length ? errs.join(' ') : null;
  }

  const present = hasValue(value);
  // Consent that is required must be ticked.
  if (field.type === 'consent' && field.required && value !== true) {
    return 'Tick the box to agree.';
  }
  if (field.required && !present) return requiredMessage(field);
  if (!present) return null;

  const str = typeof value === 'string' ? value : stringifyValue(value);

  if (field.type === 'email' && !EMAIL_RE.test(str)) {
    return 'Enter an email address like name@example.co.uk.';
  }

  const v = field.validation;
  if (v) {
    if (v.pattern) {
      try {
        if (!new RegExp(v.pattern).test(str)) {
          return `Check your ${inSentence(field.label)}. It doesn’t look right.`;
        }
      } catch {
        /* ignore an invalid author-supplied pattern */
      }
    }
    if (typeof v.minLength === 'number' && str.length < v.minLength) {
      return `Your ${inSentence(field.label)} needs at least ${v.minLength} characters.`;
    }
    if (typeof v.maxLength === 'number' && str.length > v.maxLength) {
      return `Your ${inSentence(field.label)} can be up to ${v.maxLength} characters.`;
    }
    if (field.type === 'number') {
      const n = Number(str);
      if (Number.isNaN(n)) return `Enter your ${inSentence(field.label)} as a number.`;
      if (typeof v.min === 'number' && n < v.min) {
        return `Your ${inSentence(field.label)} must be ${v.min} or more.`;
      }
      if (typeof v.max === 'number' && n > v.max) {
        return `Your ${inSentence(field.label)} must be ${v.max} or less.`;
      }
    }
  }
  return null;
}

const SAMPLE_ADDRESS = {
  line1: '1 Test Street',
  line2: '',
  line3: '',
  city: 'Testford',
  county: 'Testshire',
  postcode: 'TE1 1ST',
  country: '',
};

/** A believable placeholder value for a scalar sub-field/field type. */
function sampleScalar(type: string, label: string): unknown {
  switch (type) {
    case 'email':
      return 'test@example.com';
    case 'phone':
      return '01270 000000';
    case 'number':
      return '1';
    case 'date':
      return new Date().toISOString().slice(0, 10);
    case 'long_text':
      return 'This is a test submission.';
    default:
      return `Test ${label}`.slice(0, 60);
  }
}

/**
 * Generate sample answers for every field, used by the admin "Test form" action
 * (`/f/<slug>?test=1`) to prefill the public renderer with realistic data.
 */
export function sampleValues(fields: FieldDef[]): FormValues {
  const values: FormValues = {};
  for (const f of fields) {
    switch (f.type) {
      case 'heading':
        break;
      case 'hidden':
        if (f.defaultValue !== undefined) values[f.id] = f.defaultValue;
        break;
      case 'consent':
        values[f.id] = true;
        break;
      case 'select':
      case 'radio': {
        const opt = resolveStaticOptions(f)[0];
        if (opt) values[f.id] = opt.value;
        break;
      }
      case 'checkbox': {
        const opts = resolveStaticOptions(f);
        values[f.id] = opts.length ? [opts[0]!.value] : true;
        break;
      }
      case 'multiselect': {
        const opt = resolveStaticOptions(f)[0];
        values[f.id] = opt ? [opt.value] : [];
        break;
      }
      case 'address':
        values[f.id] = { ...SAMPLE_ADDRESS };
        break;
      case 'vat':
        values[f.id] = 'GB000000000';
        break;
      case 'company_number':
        // Left blank: a made-up number would fail the Companies House check.
        values[f.id] = '';
        break;
      case 'file':
        break;
      case 'repeater':
      case 'contact': {
        const item: RepeaterItem = {};
        for (const s of resolveRepeaterSubFields(f)) {
          item[s.id] =
            s.type === 'address' ? { ...SAMPLE_ADDRESS } : sampleScalar(s.type, s.label);
        }
        if (f.type === 'contact') item[CONTACT_PRIMARY_KEY] = true;
        values[f.id] = [item];
        break;
      }
      default:
        values[f.id] = sampleScalar(f.type, f.label);
    }
  }
  return values;
}

/**
 * Validate every visible input field on a page. `visible` is the cascading
 * visibility map for the whole form (see {@link visibleFields}), so a field
 * hidden because its driver is hidden isn't validated. Returns id → error.
 */
export function validatePage(
  page: Page,
  values: FormValues,
  visible: Record<string, boolean>,
  /** Fields not to validate here — e.g. ones locked by a company lookup. */
  skip: ReadonlySet<string> = new Set(),
  /** Contact addresses not to re-check (see `filledAddressKeys`). */
  lenientAddresses: readonly string[] = [],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of page.fields) {
    if (!isInputField(field)) continue;
    if (!visible[field.id] || skip.has(field.id)) continue;
    const err = validateField(field, values[field.id], lenientAddresses);
    if (err) errors[field.id] = err;
  }
  return errors;
}

/**
 * Build the submit payload keyed by field id: includes hidden defaults, omits
 * headings and fields hidden by `visibleWhen`, and flattens address/file values.
 */
export function buildSubmitData(
  fields: FieldDef[],
  values: FormValues,
  /**
   * What the applicant can see (the form's own `vis`, which reads the chosen
   * country's iQ details, e.g. "is in the EU"). Without it, visibility is
   * worked out from the answers alone, which drops questions shown only for
   * a country attribute; the Worker then says they're required.
   */
  visible: Record<string, boolean> = visibleFields(fields, values as Record<string, unknown>),
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === 'heading') continue;
    if (f.type === 'hidden') {
      if (f.defaultValue !== undefined) data[f.id] = f.defaultValue;
      continue;
    }
    if (!visible[f.id]) continue;
    const raw = values[f.id];
    if (raw === undefined) {
      if (f.defaultValue !== undefined) data[f.id] = f.defaultValue;
      continue;
    }
    // Drop the UI-only `sameAsFirst` flag; keep the resolved address fields.
    if (isAddressValue(raw)) {
      const a = raw as AddressValue;
      data[f.id] = {
        line1: a.line1,
        line2: a.line2,
        line3: a.line3 ?? '',
        city: a.city,
        county: a.county ?? '',
        postcode: a.postcode,
        country: a.country ?? '',
      };
    } else {
      data[f.id] = raw;
    }
  }
  return data;
}
